/**
 * 딜 연결 창 — 판매 현황의 [딜 연결] 이 연다(백엔드 V장 "딜 없는 판매" ④, 2026-11-03).
 * 판매의 거래처 딜만 보인다(거래처가 없는 판매면 전부) — 찾기 글로 좁힌다. 이미 채택 견적이 있는 딜은 못 고른다
 * (한 딜에 판매 하나 — 서버도 409 로 막는다). 저장은 하지 않는다 — 고른 딜 id 를 답으로 돌려주면 부르는 쪽이 잇는다.
 * 창은 document.body 에 붙였다가 닫으면 뗀다(취소 창과 같은 mg-* 틀).
 */
import type { DealRow, SalesOrderDetail } from '../api/client';
import * as api from '../api/client';
import { assert } from '../lib/assert';
import { escapeHtml } from '../lib/cells';

const DEAL_PICK_LIMIT = 20;           // 한 번에 보이는 딜 수 — 넘으면 찾기 글로 좁힌다
const SEARCH_DEBOUNCE_MS = 300;

/** 창을 열고 답을 기다린다. [연결] 이면 고른 딜 id(숫자), [닫기] · 바깥 · Esc 면 null */
export async function openSalesDealLinkDialog(detail: SalesOrderDetail): Promise<number | null> {
  const host = document.createElement('div');
  host.innerHTML = renderSalesDealLinkDialog(detail);
  document.body.appendChild(host);

  return new Promise((resolve) => {
    let searchTimer: ReturnType<typeof setTimeout> | null = null;
    let requestNo = 0;   // 늦게 온 찾기 답은 버린다

    function close(result: number | null): void {
      if (searchTimer !== null) clearTimeout(searchTimer);
      document.removeEventListener('keydown', handleKey);
      host.remove();
      resolve(result);
    }
    function pickedOrNull(): number | null {
      const checked = host.querySelector<HTMLInputElement>('input[name="link-deal"]:checked');
      return checked === null ? null : Number(checked.value);
    }
    function refreshConfirm(): void {
      const button = host.querySelector<HTMLButtonElement>('[data-action="deal-link-confirm"]');
      assert(button !== null, '딜 연결 창은 [연결] 을 그렸다 — renderSalesDealLinkDialog');
      button.disabled = pickedOrNull() === null;
    }
    async function loadDeals(search: string): Promise<void> {
      const myNo = ++requestNo;
      const list = listOf(host);
      list.innerHTML = '<p class="text-muted">불러오는 중…</p>';
      try {
        const response = await api.fetchDealList({
          ...(detail.customer_id === null ? {} : { customer_id: Number(detail.customer_id) }),
          ...(search === '' ? {} : { search }),
          sort: 'id', order: 'desc', pageSize: DEAL_PICK_LIMIT,
        });
        if (myNo !== requestNo) return;
        list.innerHTML = response.rows.length === 0 ? '<p class="text-muted">맞는 딜이 없습니다</p>' : response.rows.map(renderDealLine).join('');
      } catch (e) {
        if (myNo !== requestNo) return;
        list.innerHTML = `<p class="text-danger">불러오지 못했습니다 — ${escapeHtml(e instanceof Error ? e.message : String(e))}</p>`;
      }
      refreshConfirm();
    }
    function handleKey(event: KeyboardEvent): void {
      if (event.key === 'Escape') close(null);
    }
    function handleClick(event: MouseEvent): void {
      const target = (event.target as HTMLElement).closest<HTMLElement>('[data-action]');
      if (target === null) return;
      if (target.dataset.action === 'deal-link-close') close(null);
      if (target.dataset.action === 'deal-link-confirm') {
        const picked = pickedOrNull();
        if (picked !== null) close(picked);
      }
    }
    function handleInput(event: Event): void {
      const input = event.target as HTMLElement;
      if (!input.classList.contains('dl-search')) {
        refreshConfirm();
        return;
      }
      if (searchTimer !== null) clearTimeout(searchTimer);
      searchTimer = setTimeout(() => { searchTimer = null; void loadDeals((input as HTMLInputElement).value.trim()); }, SEARCH_DEBOUNCE_MS);
    }

    host.addEventListener('click', handleClick);
    host.addEventListener('input', handleInput);
    host.addEventListener('change', refreshConfirm);
    document.addEventListener('keydown', handleKey);
    refreshConfirm();
    void loadDeals('');
  });
}

/** 창 전체 — 머리 · 찾기 칸 · 딜 목록 자리 · 알림 · [닫기] [연결] */
function renderSalesDealLinkDialog(detail: SalesOrderDetail): string {
  const scope = detail.customer_name === null ? '모든 딜' : `${detail.customer_name} 의 딜`;
  return `
    <div class="mg-wrap" role="dialog" aria-label="딜 연결">
      <div class="mg-overlay" data-action="deal-link-close"></div>
      <div class="mg-dialog">
        <h3 class="mg-title">딜 연결 — ${escapeHtml(detail.quote_no)}</h3>
        <p class="mg-note">${escapeHtml(scope)} · 최근 ${DEAL_PICK_LIMIT}건</p>
        <input type="search" class="input dl-search" placeholder="딜 번호 · 이름 · 파트넘버">
        <div class="sc-items dl-deals"></div>
        <p class="mg-note">연결하면 이 판매와 견적이 그 딜에 붙고, 딜은 수주로 바뀌고 히스토리에 한 줄 남습니다. 풀기는 아직 없습니다. 판매의 거래처 · 담당자는 그대로입니다.</p>
        <div class="mg-buttons">
          <button type="button" class="btn-ghost" data-action="deal-link-close">닫기</button>
          <button type="button" class="btn-primary" data-action="deal-link-confirm">연결</button>
        </div>
      </div>
    </div>`;
}

/** 딜 한 줄 — 라디오 · 번호 · 이름 · 상태. 채택 견적이 있으면 라디오를 잠그고 그렇게 적는다 */
function renderDealLine(deal: DealRow): string {
  const bTaken = deal.accepted_quote_id !== null;
  return `
          <label class="sc-item${bTaken ? ' text-muted' : ''}">
            <input type="radio" name="link-deal" value="${escapeHtml(deal.id)}"${bTaken ? ' disabled' : ''}>
            <span class="mono">${escapeHtml(deal.deal_no)}</span>
            <span class="sc-item-name">${escapeHtml(deal.name)}</span>
            <span class="text-muted">${escapeHtml(deal.status)}</span>
            ${bTaken ? '<span class="sc-item-gone">채택 견적 있음</span>' : ''}
          </label>`;
}

function listOf(host: HTMLElement): HTMLElement {
  const list = host.querySelector<HTMLElement>('.dl-deals');
  assert(list !== null, '딜 연결 창은 딜 목록 자리(.dl-deals)를 그렸다 — renderSalesDealLinkDialog');
  return list;
}
